// ChannelList.tsx
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import {
    Avatar,
    Box,
    IconButton,
    Skeleton,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { Client as ConversationsClient } from "@twilio/conversations";
import { User } from "lib/features/user/types";
import { selectUser } from "lib/features/user/userSlice";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

// Your API service (returns your example array)
import chatServices from "services/chat-service";

// Conversation view
import { parseImageUrl } from "util/parseImageUrl";
import ConversationView from "./MessageView";

// ---- Twilio client singleton + connection gating ----
let convClient: ConversationsClient | null = null;

async function getOrInitTwilioClient(getToken: () => Promise<string>) {
  if (convClient) return convClient;

  const token = await getToken();
  convClient = new ConversationsClient(token);

  // Token refresh
  convClient.on("tokenAboutToExpire", async () => {
    try {
      await convClient?.updateToken(await getToken());
    } catch (e) {
      console.error("Failed to refresh token (aboutToExpire):", e);
    }
  });
  convClient.on("tokenExpired", async () => {
    try {
      await convClient?.updateToken(await getToken());
    } catch (e) {
      console.error("Failed to refresh token (expired):", e);
    }
  });

  // If already connected, short-circuit
  if ((convClient as any).connectionState === "connected") return convClient;

  // Wait for connection (prevents `myConversationsRead` undefined)
  await new Promise<void>((resolve, reject) => {
    const onState = (state: any) => {
      if (state === "connected") {
        convClient?.off("connectionStateChanged", onState);
        resolve();
      } else if (state === "denied") {
        convClient?.off("connectionStateChanged", onState);
        reject(new Error("Twilio Conversations connection denied"));
      }
    };
    convClient?.on("connectionStateChanged", onState);
  });

  return convClient;
}

export type ConversationViewProps = {
  conversationSid: string;
  getClient: () => Promise<ConversationsClient>;
  currentUserId: string;
};

// ---- Types for your API payload ----
type ApiConversation = {
  sid: string;
  friendlyName: string | null;
  participants: string[]; // user ids as strings
  myUser: any;
  participantsDetails: any[]; // full user objects
};

export default function ChannelList() {
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width: 899px)"); // <900px = single pane
  const navigate = useNavigate();

  // Support either :sid or :channelUrl param naming
  const params = useParams();
  const routeSidParam =
    (params.sid as string | undefined) || (params.channelUrl as string | undefined);

  const reduxUser = useSelector(selectUser);
  const userData = localStorage.getItem("user") as string | null;
  const parsedUser: User | undefined = userData ? JSON.parse(userData) : undefined;
  const currentUserId = String(parsedUser?.id ?? reduxUser?.id ?? "");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<ApiConversation[]>([]);

  // Twilio token fetcher (must mint a Conversations token tied to your Service SID)
  const fetchToken = useRef<() => Promise<string>>(async () => {
    if (!currentUserId) throw new Error("No current user found");
    const res = await chatServices.getUserTokenForChat(currentUserId);
    return String(res.data.token);
  });

  // Fetch the conversations list from your API
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        if (!currentUserId) throw new Error("Missing current user id");

        const resp = await chatServices.getConversations(Number(currentUserId));
        if (!alive) return;
        setRows(Array.isArray(resp?.data) ? resp.data : []);
        setError(null);
      } catch (e: any) {
        if (!alive) return;
        setError(e?.message || "Failed to load conversations");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [currentUserId]);

  // Derive items for the list: display “other participant” name
  const items = useMemo(() => {
    return rows.map((c) => {
      const other =
        c.participantsDetails.find((p) => String(p.id) !== String(currentUserId)) ??
        c.participantsDetails[0];

      let displayName = "";
      let avatar = "";

      const hasBusinessUser = Array.isArray(other?.business_users) && other.business_users.length > 0;
      const businessUser = hasBusinessUser ? other.business_users[0] : undefined;
      const business = businessUser?.business;

      if (business) {
        displayName = business.business || "Conversation";
        const businessImages = Array.isArray(business.businessImages)
          ? business.businessImages
          : [];
        avatar = businessImages[0]?.uri || "";
      } else {
        displayName =
          (other?.first_name || other?.last_name)
            ? `${other?.first_name ?? ""} ${other?.last_name ?? ""}`.trim()
            : other?.email || "Conversation";
        avatar = other?.avatar || "";
      }

      const myBusinessUser = Array.isArray(c.myUser?.business_users)
        ? c.myUser?.business_users[0]
        : undefined;
      const myBusiness = myBusinessUser?.business;
      const myBusinessImages = Array.isArray(myBusiness?.businessImages)
        ? myBusiness?.businessImages
        : [];

      return {
        sid: c.sid,
        name: displayName || "Conversation",
        avatar,
        myavatar: myBusinessImages[0]?.uri || c.myUser?.avatar || "",
      };
    });
  }, [rows, currentUserId]);

  const renderLoading = () => (
    <Box sx={{ p: 2, display: "grid", gap: 1.5 }}>
      {Array.from({ length: 8 }).map((_, i) => (
        <Skeleton key={i} variant="rectangular" width="100%" height={64} sx={{ borderRadius: 1 }} />
      ))}
    </Box>
  );
  console.log(userData)
  const handleOpen = (sid: string, avatar: string, name:string, myavatar:string) => navigate(`/chat/${sid}/${avatar}/${name}/${myavatar}`);
  const handleBackToList = () => navigate("/chat");

  // ---- Layout rules ----
  // Desktop: 2-pane grid
  // Mobile: if no routeSidParam → show list; if routeSidParam → show conversation full screen
  if (isMobile) {
    return (
      <Box
        sx={{
          height: "100dvh",
          display: "flex",
          flexDirection: "column",
          borderColor: theme.palette.divider,
        }}
      >
        {!routeSidParam ? (
          // LIST fullscreen
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <Box sx={{ p: 2, fontWeight: 700, fontSize: 18, borderBottom: `1px solid ${theme.palette.divider}` }}>
              Messages
            </Box>
            <Box sx={{ overflowY: "auto", flex: 1 }}>
              {loading ? (
                renderLoading()
              ) : error ? (
                <Box sx={{ px: 2, py: 2, color: "error.main" }}>{error}</Box>
              ) : items.length === 0 ? (
                <Box sx={{ px: 2, py: 2, color: "text.secondary" }}>No conversations yet.</Box>
              ) : (
                <Box sx={{ display: "grid" }}>
                  {items.map((it) => (
                    <Box
                      key={it.sid}
                      onClick={() => handleOpen(it.sid, it.avatar, it.name, it.myavatar)}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "48px 1fr",
                        gap: 1.25,
                        alignItems: "center",
                        px: 2,
                        py: 1.25,
                        cursor: "pointer",
                        "&:hover": { background: theme.palette.action.hover },
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Avatar src={parseImageUrl(it.avatar)} alt={it.name} sx={{ width: 40, height: 40 }} />
                      <Box>
                        <Typography sx={{ fontWeight: 600, lineHeight: 1.2 }}>{it.name}</Typography>
                        <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
                          Tap to view messages
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          // CONVERSATION fullscreen with back header
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                px: 1,
                py: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <IconButton size="small" onClick={handleBackToList} aria-label="Back">
                <ArrowBackIosNewIcon fontSize="small" />
              </IconButton>
              <Typography sx={{ fontWeight: 700 }}>Messages</Typography>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <ConversationView
                conversationSid={routeSidParam}
                getClient={() => getOrInitTwilioClient(fetchToken.current)}
                currentUserId={currentUserId}
                avatar=""
                name=""

              />
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  // Desktop split view
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "360px 1fr",
        height: "100dvh",
        borderColor: theme.palette.divider,
      }}
    >
      {/* Left: List */}
      <Box sx={{ borderRight: `1px solid ${theme.palette.divider}`, overflow: "auto" }}>
        <Box sx={{ p: 2, fontWeight: 700, fontSize: 18 }}>Messages</Box>
        {loading ? (
          renderLoading()
        ) : error ? (
          <Box sx={{ px: 2, color: "error.main" }}>{error}</Box>
        ) : items.length === 0 ? (
          <Box sx={{ px: 2, color: "text.secondary" }}>No conversations yet.</Box>
        ) : (
          <Box sx={{ display: "grid" }}>
            {items.map((it) => (
              <Box
                key={it.sid}
                onClick={() => handleOpen(it.sid, it.avatar, it.name, it.myavatar)}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "48px 1fr",
                  gap: 1.25,
                  alignItems: "center",
                  px: 2,
                  py: 1.25,
                  cursor: "pointer",
                  "&:hover": { background: theme.palette.action.hover },
                  background:
                    routeSidParam === it.sid ? theme.palette.action.selected : "transparent",
                }}
              >
                <Avatar src={parseImageUrl(it.avatar)}  alt={it.name} sx={{ width: 40, height: 40 }} />
                <Box>
                  <Typography sx={{ fontWeight: 600, lineHeight: 1.2 }}>{it.name}</Typography>
                  <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
                    Click to view messages
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Right: Conversation panel */}
      <Box sx={{ overflow: "hidden" }}>
        {routeSidParam ? (
          <ConversationView
            conversationSid={routeSidParam}
            getClient={() => getOrInitTwilioClient(fetchToken.current)}
            currentUserId={currentUserId}
            avatar={""}
            name={""} 
          />
        ) : (
          <Box sx={{ display: "grid", placeItems: "center", height: "100%" }}>
            <Typography sx={{ color: "text.secondary" }}>
              Select a conversation to start chatting.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}